import * as React from 'react';
import { Input, Box, createStyles } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useId } from '@mantine/hooks';

// Components
import { InputValueTable } from '../Form/components/InputValueTable';
import { ActionButtons } from '../Form/components/ActionButtons';
import { FormScaffolding } from '../Form/FormScaffolding';

// Helpers
import { hydrateManifestSection } from '../Form/helpers/hydrateManifestSection';

// Hooks
import { useStaticKey } from '../Form/hooks/useStaticKey';

// Styles
import { getCommonFieldStyles } from '../Form/helpers/fieldStyles';

// Types
import { IManifestFieldHydrated, IManifestField } from '../../manifests/types';
import { ISurveyTargetPeriod } from '../../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        label: {
            ...commonFieldStyles.label,
            top: '1px',
            left: '1px',
            right: '21px',
            borderRadius: '4px',
            background: '#fff'
        }
    };
});

interface DefaultSubFormProps<FormValues> {
    data?: any[];
    ancillaryData?: Record<string, any>;
    subManifest: IManifestFieldHydrated[] | IManifestField[];
    parentform: UseFormReturnType<FormValues>;
    parentNext?: (data?: Record<string, any>) => Promise<void>;
    parentPrev?: () => void;
    validator?: (
        val?: any,
        values?: Record<string, any>,
        requiredValues?: string[]
    ) => Record<string, string | null> | string | null;
    activeStep?: number;
    surveyId?: string;
    clientId?: string;
    formId?: string;
    trigger?: string;
    targetperiod?: ISurveyTargetPeriod;
}

export function DefaultSubForm<FormValues extends Record<string, any>>(props: DefaultSubFormProps<FormValues>) {
    const { convertMapFromKeysToNames, convertMapFromNamesToKeys } = useStaticKey();
    const {
        data,
        subManifest,
        ancillaryData,
        formId,
        parentNext,
        parentPrev,
        activeStep,
        surveyId,
        clientId,
        trigger,
        parentform,
        targetperiod,
        validator
    } = props;
    const formProps = React.useMemo(
        () => ({
            data: convertMapFromNamesToKeys(parentform?.values),
            ancillaryData,
            formId,
            isSubForm: true,
            hideCancel: true,
            progressiveForm: false,
            dirtySaveOnly: false,
            saveLabelKey: 'saveContinue',
            sections: [{ manifest: hydrateManifestSection(subManifest) }],
            formInsert: false,
            surveyId,
            clientId,
            trigger
        }),
        [subManifest, surveyId, hydrateManifestSection, trigger, data, ancillaryData, formId, clientId]
    );
    const onSave = React.useCallback(
        ({ values, furthestStep }: { values: Record<string, any>; furthestStep: number }): Promise<any> => {
            const updatedState = {
                ...parentform?.values,
                ...convertMapFromKeysToNames(values)
            };

            // console.log('parentform', parentform);
            parentform.setValues(updatedState);
            // console.log('parentform?.values', parentform?.values);

            // debugger;
            parentNext?.(updatedState);
            return Promise.resolve();
        },
        [parentform?.values]
    );

    return (
        <FormScaffolding
            {...formProps}
            noCardStyle
            onSave={onSave}
            hideDefaultBack
            preventStepProgression
            saveLabelKey={'saveContinue'}
            parentPrev={parentPrev}
            targetPeriod={targetperiod}
        />
    );
}
